import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays } from "lucide-react";

import { CurrencyValue } from "interfaces/currency";

import { Reservation } from "models/reservations";

interface Currency {
	value: string;
	label: string;
}

const currencyItems = [
	{ value: CurrencyValue.USD, label: "$" },
	{ value: CurrencyValue.AMD, label: "֏" },
	{ value: CurrencyValue.GBP, label: "£" },
] as Array<Currency>;

interface Props {
	data: Reservation;
	onEditClick: (reservation: Reservation) => void;
	showProperty?: boolean;
	propertyName?: string;
	propertyId?: string;
}

const cellClass =
	"bg-transparent text-center h-[50px] flex items-center justify-center w-full shrink-0 flex-col [&>a]:no-underline";

const ReservationTableItem = (props: Props): JSX.Element => {
	const { data, onEditClick, showProperty, propertyName, propertyId } = props;

	const {
		checkin_time,
		checkout_time,
		total_price,
		payout_price,
		channel,
		status,
		code,
		nights,
	} = data;

	const currentCurrency = currencyItems.find(
		(item) => item.value === data.currency
	) as Currency;

	const [currency, setCurrency] = useState(currentCurrency);

	const handleEditClick = () => {
		onEditClick(data);
	};

	const columns = useMemo(() => {
		const countOfColumns = showProperty ? 12 : 11;

		let columnsString = "";

		for (let index = 0; index < countOfColumns; index++) {
			columnsString += index > 0 ? " 1fr" : "1fr";
		}

		return columnsString;
	}, [showProperty]);

	const renderActions = () => (
		<button
			onClick={handleEditClick}
			className="rounded-full h-7 w-7 flex justify-center items-center bg-transparent cursor-pointer border border-border"
		>
			<CalendarDays className="h-3 w-3 text-muted-foreground" />
		</button>
	);

	const renderPropertyName = () =>
		showProperty ? (
			<div className={cellClass}>
				<Link to={`/properties/${propertyId || ""}`}>
					<p className="text-sm text-muted-foreground">{propertyName || ""}</p>
				</Link>
			</div>
		) : null;

	const renderStatus = () => {
		return (
			<p className="text-sm text-foreground">{status}</p>
		);
	};

	return (
		<div
			className="grid gap-px border-b border-border transition-colors hover:bg-[#0d1525] hover:cursor-pointer"
			style={{ gridTemplateColumns: columns }}
		>
			{renderPropertyName()}
			<div className={cellClass}>
				<p className="text-sm text-muted-foreground">{channel}</p>
			</div>
			<div className={cellClass}>
				<p className="text-sm text-muted-foreground">{code}</p>
			</div>
			<div className={cellClass}>{renderStatus()}</div>
			<div className={cellClass}>
				<p className="text-[13px] text-foreground mb-0.5">
					{`${data.guest.first_name} ${data.guest.last_name}`}
				</p>
				<p className="text-[13px] text-muted-foreground">{`${data.adults} adults`}</p>
			</div>
			<div className={cellClass}>
				<p className="text-sm text-muted-foreground">{nights}</p>
			</div>
			<div className={cellClass}>
				<p className="text-sm text-muted-foreground">{dayjs(checkin_time).format("D MMM, YYYY")}</p>
			</div>
			<div className={cellClass}>
				<p className="text-sm text-muted-foreground">{dayjs(checkout_time).format("D MMM, YYYY")}</p>
			</div>
			<div className={cellClass}>
				<p className="text-sm text-muted-foreground">{currency.label}</p>
			</div>
			<div className={cellClass}>
				<p className="text-sm text-foreground">
					{currency.label}
					{!(total_price % 1) ? +total_price : (+total_price).toFixed(1)}
				</p>
			</div>
			<div className={cellClass}>
				<p className="text-sm text-foreground">
					{currency.label}
					{!(payout_price % 1) ? +payout_price : (+payout_price).toFixed(1)}
				</p>
			</div>
			<div className="h-[50px] bg-transparent flex justify-center items-center">
				{renderActions()}
			</div>
		</div>
	);
};

export default ReservationTableItem;
